import { Injectable } from '@angular/core';
import { ConsentService, ConsentType, UserConsentResponseDto } from '@markant-platform/consent/data-access';
import { catchError, forkJoin, Observable, of, switchMap, throwError } from 'rxjs';
import { DialogService } from '@markant-platform/shared/dialog/feature';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { Router, UrlTree } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ActiveConsentGuard {
  constructor(
    private readonly authenticatedService: AuthenticatedUserService,
    private readonly consentService: ConsentService,
    private readonly dialogService: DialogService,
    private readonly router: Router
  ) {}

  /**
   * when the user as no active consent a dialog for the consent is shown
   *
   * @returns {Observable<boolean>}
   */
  canActivate(): Observable<boolean | UrlTree> {
    const userError = 'user_error';
    return forkJoin([
      this.authenticatedService.getUser().pipe(
        catchError(err => {
          return throwError(() => {
            return { ...err, type: userError };
          });
        })
      ),
      this.authenticatedService.getOrganizationId(),
    ]).pipe(
      switchMap(([user, organizationId]) => {
        if (user.organizationMemberships?.some(membership => membership.organizationId === organizationId)) {
          return this.consentService.getConsentForUserInOrganization(user.id || '', organizationId);
        }
        return throwError(() => 'no_membership');
      }),
      switchMap(consents => {
        if (
          this.consentsIncludeType(consents, ConsentType.PLATFORM_TERMS_OF_USE) &&
          this.consentsIncludeType(consents, ConsentType.PLATFORM_PRIVACY_NOTICE)
        ) {
          return of(true);
        }
        return this.dialogService.showInitialConsentDialog();
      }),
      catchError(err => {
        if (err.type !== userError) {
          return of(true);
        }
        let reason = 'service-error';
        if (err.status === 404) {
          reason = 'not_found';
        }

        if (err.status === 403 && err.error.errorCode === 'USE-075') {
          reason = 'not-active';
        }
        return this.redirectToErrorPage(reason);
      })
    );
  }

  private consentsIncludeType(consents: UserConsentResponseDto[], consentType: ConsentType): boolean {
    return consents.some(consent => consent.consentType === consentType);
  }

  private redirectToErrorPage(reason: string): Observable<UrlTree> {
    return of(
      this.router.createUrlTree(['error'], {
        queryParams: {
          reason: reason,
          logout: true,
        },
      })
    );
  }
}
